import { StateCreator } from "zustand"
import toast from "react-hot-toast"
import Swal from "sweetalert2"

import type * as ChatTypes from "@pages/Chat/Chat.types"
import type * as StoreTypes from "@src/types/store"

import { apiService } from "@src/services/apiService"
import { SwalConfig } from "@src/utils/SwalFire"
import { preConfirmDeleteExpress } from "@src/utils"

const initState = {
  latestMessages: new Map(),
  chatLoading: null,
  interactions: [],
  paginationInteractions: null,
  reachedLimitInteractions: false,
  chatData: new Map(),
  interactionQueryParams: {
    legacyProfileId: "",
    limit: 30,
    page: 1,
  },
}

export const createInteractionsSlice: StateCreator<
  StoreTypes.StoreState,
  [],
  [],
  StoreTypes.InteractionsSlice
> = (set, get) => ({
  ...initState,
  setChatLoading: (loading) => {
    set({ chatLoading: loading })
  },
  setInteractionQueryParams: (params: Partial<ChatTypes.IQueryParamsChat>) => {
    set((state) => ({ interactionQueryParams: { ...state.interactionQueryParams, ...params } }))
  },
  getLatestMessages: async () => {
    try {
      const { chatLoading } = get()
      if (chatLoading === "FETCHING_LATEST_MESSAGES" || get().latestMessages.size > 0) return
      set({ chatLoading: "FETCHING_LATEST_MESSAGES" })
      const response = await apiService.interactions.latest()
      const { data } = response.data as BaseResponseExpress<ChatTypes.IChat[]>
      set({ latestMessages: new Map(data.map((interaction) => [interaction.legacyProfileId, interaction])) })
    } catch (error) {
      toast.error("Error fetching latest messages")
    } finally {
      set({ chatLoading: null })
    }
  },
  getInteractions: async (page = 1, callback) => {
    const { loading, interactionQueryParams, currentProfile, reachedLimitInteractions } = get()
    if (loading === "FETCHING_INTERACTIONS" || reachedLimitInteractions || !currentProfile) return
    try {
      set({ loading: "FETCHING_INTERACTIONS" })

      const response = await apiService.interactions.getCollection({
        ...interactionQueryParams,
        legacyProfileId: currentProfile.id,
        page,
      })

      const { data, pagination } = response.data as ResponseExpressPagination<ChatTypes.IChat[]>
      const newReachedLimit = !pagination.hasNextPage
      const interactionsUpdated = [...data.reverse(), ...get().interactions]

      set({
        interactionQueryParams: { ...interactionQueryParams, page },
        interactions: interactionsUpdated,
        paginationInteractions: pagination,
        reachedLimitInteractions: newReachedLimit,
      })
      callback?.(interactionsUpdated)
    } catch (error) {
      toast.error("Error fetching interactions")
    } finally {
      set({ loading: null })
    }
  },
  updateLatestMessage: (legacyProfileId: string, interaction: ChatTypes.IChat) => {
    set((state) => {
      const newLatestMessages = new Map(state.latestMessages)
      const currentLatest = newLatestMessages.get(legacyProfileId)
      if (!currentLatest || new Date(interaction.createdAt) > new Date(currentLatest.createdAt)) {
        newLatestMessages.set(legacyProfileId, interaction)
      }
      return { latestMessages: newLatestMessages }
    })
  },
  addOptimisticInteraction: (interaction: ChatTypes.IChat) => {
    set((state) => {
      const newInteractions = [...state.interactions, interaction]
      const newLatestMessages = new Map(state.latestMessages)
      const currentLatest = newLatestMessages.get(interaction.legacyProfileId)
      if (!currentLatest || new Date(interaction.createdAt) > new Date(currentLatest.createdAt)) {
        newLatestMessages.set(interaction.legacyProfileId, interaction)
      }
      return { interactions: newInteractions, latestMessages: newLatestMessages }
    })
  },
  replaceOptimisticInteraction: (tempId: string, realInteractions: ChatTypes.IChat[]) => {
    set((state) => {
      const filteredInteractions = state.interactions.filter((interaction) => interaction._id !== tempId)
      const newInteractions = [...filteredInteractions, ...realInteractions]
      const newLatestMessages = new Map(state.latestMessages)

      realInteractions.forEach((interaction) => {
        const currentLatest = newLatestMessages.get(interaction.legacyProfileId)
        if (!currentLatest || new Date(interaction.createdAt) > new Date(currentLatest.createdAt)) {
          newLatestMessages.set(interaction.legacyProfileId, interaction)
        }
      })

      return { interactions: newInteractions, latestMessages: newLatestMessages }
    })
  },
  removeOptimisticInteraction: (tempId: string) => {
    set((state) => {
      const filteredInteractions = state.interactions.filter((interaction) => interaction._id !== tempId)
      const removedInteraction = state.interactions.find((interaction) => interaction._id === tempId)
      const newLatestMessages = new Map(state.latestMessages)

      if (removedInteraction) {
        const profileInteractions = filteredInteractions.filter(
          (interaction) => interaction.legacyProfileId === removedInteraction.legacyProfileId,
        )
        if (profileInteractions.length > 0) {
          const latestForProfile = profileInteractions.reduce((latest, current) => {
            return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest
          })
          newLatestMessages.set(removedInteraction.legacyProfileId, latestForProfile)
        } else {
          newLatestMessages.delete(removedInteraction.legacyProfileId)
        }
      }

      return { interactions: filteredInteractions, latestMessages: newLatestMessages }
    })
  },
  createInteraction: async (payload, tempId?: string) => {
    try {
      if (get().loading === "CREATING_INTERACTION") return
      set({ loading: "CREATING_INTERACTION" })
      const response = await apiService.interactions.create(payload)
      const { data } = response.data as BaseResponseExpress<ChatTypes.IChat[]>

      if (tempId) {
        get().replaceOptimisticInteraction(tempId, data)
      } else {
        set((state) => {
          const newInteractions = [...state.interactions, ...data]
          const newLatestMessages = new Map(state.latestMessages)

          data.forEach((interaction) => {
            const currentLatest = newLatestMessages.get(interaction.legacyProfileId)
            if (!currentLatest || new Date(interaction.createdAt) > new Date(currentLatest.createdAt)) {
              newLatestMessages.set(interaction.legacyProfileId, interaction)
            }
          })

          return { interactions: newInteractions, latestMessages: newLatestMessages }
        })
      }
    } catch (err: unknown) {
      if (tempId) get().removeOptimisticInteraction(tempId)
      throw err
    } finally {
      set({ loading: null })
    }
  },
  deleteInteraction: async (id) => {
    try {
      const result = await Swal.fire(
        SwalConfig.confirmDestroy({
          preConfirm: preConfirmDeleteExpress.bind(null, {
            promise: apiService.interactions.delete,
            resource: "Chet Message",
            id,
          }),
        }),
      )
      if (!result.isConfirmed) return

      set((state) => {
        const deletedInteraction = state.interactions.find((interaction) => interaction._id === id)
        const filteredInteractions = state.interactions.filter((interaction) => interaction._id !== id)
        const newLatestMessages = new Map(state.latestMessages)

        if (deletedInteraction) {
          const profileInteractions = filteredInteractions.filter(
            (interaction) => interaction.legacyProfileId === deletedInteraction.legacyProfileId,
          )
          if (profileInteractions.length > 0) {
            const latestForProfile = profileInteractions.reduce((latest, current) => {
              return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest
            })
            newLatestMessages.set(deletedInteraction.legacyProfileId, latestForProfile)
          } else {
            newLatestMessages.delete(deletedInteraction.legacyProfileId)
          }
        }

        return { interactions: filteredInteractions, latestMessages: newLatestMessages }
      })

      toast.success("Interaction deleted successfully")
    } catch (error) {
      toast.error("Error deleting interaction. Please try again.")
    } finally {
      set({ loading: null })
    }
  },
  cancelInteraction: async (uuid) => {
    try {
      set({ loading: "CANCELING_INTERACTION" })
      await apiService.interactions.cancel(uuid)
    } catch (error) {
      toast.error("Error canceling interaction")
    } finally {
      set({ loading: null })
    }
  },
  resetInteractions: () => {
    const prevLatestMessages = new Map(get().latestMessages)
    set({ ...initState, latestMessages: prevLatestMessages })
  },
})
